import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Car, HardDrive, ShieldAlert } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { DeviceStateBadge } from '@/components/domain/DeviceStateBadge';
import { SeverityBadge } from '@/components/domain/SeverityBadge';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { getCustomer } from '@/api/endpoints/admin';
import { queryKeys } from '@/api/queryKeys';
import { titleCase } from '@/lib/format';

/**
 * One account, for when the phone rings.
 *
 * There is no map and no telemetry here on purpose. Answering "is their device
 * working" does not require watching where somebody drove, and a support screen that
 * shows live location of any customer on demand is a stalking tool (invariant #4).
 * Location stays on the device pages, behind the ownership check.
 */
export function CustomerDetailPage() {
  const { customerId } = useParams();
  const id = Number(customerId);

  const detail = useQuery({
    queryKey: queryKeys.customer(id),
    queryFn: () => getCustomer(id),
    enabled: Number.isInteger(id) && id > 0,
  });

  if (detail.isPending) {
    return (
      <>
        <PageHeader title="Customer" />
        <SkeletonCard />
      </>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <>
        <PageHeader title="Customer" />
        <Card>
          <ErrorState error={detail.error} onRetry={detail.refetch} />
        </Card>
      </>
    );
  }

  const { customer, vehicles, devices, recent_crashes, emergency_contact_name, emergency_contact_phone } =
    detail.data;

  return (
    <>
      <Link
        to="/admin/customers"
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-ink-secondary hover:text-ink"
      >
        <ArrowLeft className="size-4" aria-hidden />
        All customers
      </Link>

      <PageHeader
        title={customer.name}
        description={`${customer.email} · ${titleCase(customer.role)} · joined ${new Date(customer.created_at).toLocaleDateString()}`}
      />

      <div className="grid gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Contact" />
            <CardBody>
              <dl className="space-y-2.5 text-[13px]">
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-secondary">Their mobile</dt>
                  <dd className="text-ink" data-numeric>
                    {customer.phone ?? <span className="text-ink-muted">Not set</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-secondary">Emergency contact</dt>
                  <dd className="text-right">
                    {emergency_contact_phone ? (
                      <>
                        <span className="block text-ink">{emergency_contact_name ?? 'Unnamed'}</span>
                        <span className="block text-ink-secondary" data-numeric>
                          {emergency_contact_phone}
                        </span>
                      </>
                    ) : (
                      <span className="text-warn">
                        Not set — a critical crash reaches only them
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Vehicles" />
            {vehicles.length === 0 ? (
              <CardBody>
                <p className="flex items-center gap-2 text-[13px] text-ink-muted">
                  <Car className="size-4" aria-hidden />
                  No vehicles registered.
                </p>
              </CardBody>
            ) : (
              <ul className="divide-y divide-line">
                {vehicles.map((vehicle) => (
                  <li key={vehicle.id} className="flex justify-between gap-3 px-4 py-2.5 text-[13px]">
                    <span className="font-medium text-ink" data-numeric>
                      {vehicle.license_plate}
                    </span>
                    <span className="text-ink-secondary">{vehicle.vehicle_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card>
          <CardHeader title="Devices" description="Currently held by this account." />
          {devices.length === 0 ? (
            <EmptyState
              icon={HardDrive}
              title="No devices"
              description="This account has not claimed a device."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="border-b border-line text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">State</th>
                    <th className="px-4 py-2 font-medium">Serial</th>
                    <th className="hidden px-4 py-2 font-medium md:table-cell">Batch</th>
                    <th className="hidden px-4 py-2 font-medium lg:table-cell">Claimed</th>
                    <th className="px-4 py-2 font-medium">Last seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {devices.map((device) => (
                    <tr key={device.id} className="transition-colors hover:bg-surface-sunken">
                      <td className="px-4 py-2.5">
                        <DeviceStateBadge state={device.state} />
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-ink-secondary">
                        {device.serial_number}
                      </td>
                      <td className="hidden px-4 py-2.5 text-ink-secondary md:table-cell">
                        {device.hw_batch ?? '—'}
                      </td>
                      <td className="hidden px-4 py-2.5 text-ink-secondary lg:table-cell">
                        <RelativeTime value={device.claimed_at} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className="flex items-center gap-2">
                          <OnlineDot online={device.is_online} />
                          <span className="text-ink-muted">
                            <RelativeTime value={device.last_seen} />
                          </span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Recent crashes"
            description="Their ten most recent, with how the alerts went."
          />
          {recent_crashes.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="No crashes recorded"
              description="Nothing on this account has cleared the 3.5 g threshold."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="border-b border-line text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">Severity</th>
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-4 py-2 font-medium">Alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {recent_crashes.map((crash) => (
                    <tr key={crash.id} className="transition-colors hover:bg-surface-sunken">
                      <td className="px-4 py-2.5">
                        <SeverityBadge severity={crash.severity} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-ink-secondary">
                        <RelativeTime value={crash.timestamp} />
                      </td>
                      <td className="px-4 py-2.5">
                        {crash.alerts_failed > 0 ? (
                          <Link
                            to={`/crashes/${crash.id}`}
                            className="font-medium text-bad hover:underline"
                          >
                            {crash.alerts_failed} of {crash.alerts_total} failed
                          </Link>
                        ) : (
                          <span className="text-ink-secondary">
                            {crash.alerts_total} delivered
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
